// Offline Storage Utilities for Resourcify
// Handles IndexedDB for offline data persistence

const DB_NAME = 'resourcify-offline';
const DB_VERSION = 1;

// Store names
const STORES = {
  RESOURCES: 'resources',
  RECYCLABLES: 'recyclables',
  BUDGET_PLANS: 'budget-plans',
  MESH_MESSAGES: 'mesh-messages',
  OFFLINE_QUEUE: 'offline-queue',
  USER_PREFERENCES: 'user-preferences'
};

// Initialize IndexedDB
export function initDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Create object stores if they don't exist
      if (!db.objectStoreNames.contains(STORES.RESOURCES)) {
        const resourceStore = db.createObjectStore(STORES.RESOURCES, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        resourceStore.createIndex('timestamp', 'timestamp', { unique: false });
        resourceStore.createIndex('category', 'category', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.RECYCLABLES)) {
        const recyclableStore = db.createObjectStore(STORES.RECYCLABLES, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        recyclableStore.createIndex('timestamp', 'timestamp', { unique: false });
        recyclableStore.createIndex('material', 'material', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.BUDGET_PLANS)) {
        const budgetStore = db.createObjectStore(STORES.BUDGET_PLANS, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        budgetStore.createIndex('timestamp', 'timestamp', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.MESH_MESSAGES)) {
        const meshStore = db.createObjectStore(STORES.MESH_MESSAGES, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
        meshStore.createIndex('timestamp', 'timestamp', { unique: false });
        meshStore.createIndex('sender', 'sender', { unique: false });
      }

      if (!db.objectStoreNames.contains(STORES.OFFLINE_QUEUE)) {
        db.createObjectStore(STORES.OFFLINE_QUEUE, { 
          keyPath: 'id', 
          autoIncrement: true 
        });
      }

      if (!db.objectStoreNames.contains(STORES.USER_PREFERENCES)) {
        db.createObjectStore(STORES.USER_PREFERENCES, { 
          keyPath: 'key' 
        });
      }
    };
  });
}

// Generic CRUD operations
export async function saveData(storeName, data) {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    
    const dataWithTimestamp = {
      ...data,
      timestamp: data.timestamp || Date.now()
    };
    
    const request = store.add(dataWithTimestamp);
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getData(storeName, id) {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(id);
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getAllData(storeName) {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function updateData(storeName, data) {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    
    const dataWithTimestamp = {
      ...data,
      updatedAt: Date.now()
    };
    
    const request = store.put(dataWithTimestamp);
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function deleteData(storeName, id) {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(id);
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export async function clearStore(storeName) {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([storeName], 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.clear();
    
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

// Offline queue for API calls when offline
export async function queueOfflineAction(action) {
  return saveData(STORES.OFFLINE_QUEUE, {
    ...action,
    timestamp: Date.now(),
    synced: false
  });
}

export async function getOfflineQueue() {
  return getAllData(STORES.OFFLINE_QUEUE);
}

export async function clearOfflineQueue() {
  return clearStore(STORES.OFFLINE_QUEUE);
}

// User preferences
export async function savePreference(key, value) {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.USER_PREFERENCES], 'readwrite');
    const store = transaction.objectStore(STORES.USER_PREFERENCES);
    const request = store.put({ key, value, timestamp: Date.now() });
    
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function getPreference(key) {
  const db = await initDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORES.USER_PREFERENCES], 'readonly');
    const store = transaction.objectStore(STORES.USER_PREFERENCES);
    const request = store.get(key);
    
    request.onsuccess = () => resolve(request.result?.value);
    request.onerror = () => reject(request.error);
  });
}

// Network status detection
export function isOnline() {
  return navigator.onLine;
}

export function onNetworkChange(callback) {
  window.addEventListener('online', () => callback(true));
  window.addEventListener('offline', () => callback(false));
}

// Sync offline data when online
export async function syncOfflineData() {
  if (!isOnline()) {
    console.log('Cannot sync - still offline');
    return;
  }

  const queue = await getOfflineQueue();
  console.log(`Syncing ${queue.length} offline actions...`);

  for (const action of queue) {
    try {
      await fetch(action.url, {
        method: action.method,
        headers: action.headers,
        body: action.body
      });
      
      // Remove synced action
      await deleteData(STORES.OFFLINE_QUEUE, action.id);
      console.log('Synced action:', action.id);
    } catch (error) {
      console.error('Failed to sync action:', action.id, error);
    }
  }

  console.log('Offline sync complete');
}

// Auto-sync when online
onNetworkChange((online) => {
  if (online) {
    console.log('Network restored - syncing offline data...');
    syncOfflineData();
  } else {
    console.log('Network lost - entering offline mode');
  }
});

export { STORES };
